import { IApiBaseProps, IUpdateRequestBase } from '@common/api.models';
import {
  BooleanFilter,
  DateTimeFilter,
  OptionsFilter,
  StringFilter,
  StringsFilter,
} from '@common/searchFilter/searchFilter.models';
import { IProjectCompany, IProjectCompanyBase } from '@company/projectCompany/projectCompany.models';
import { IMasterScheduleOption, IProjectMasterScheduleBase } from '@masterSchedule/masterSchedule.models';
import { IProjectTeamBase } from '@projectTeam/projectTeam.models';
import { IProjectRole } from '@role/projectRole/projectRole.models';
import { IProjectUserProfile } from '@userProfile/projectUserProfile.models';
import { IUserProfile } from '@userProfile/userProfile.models';
import { IProjectDesignModificationWithLink } from '@designModification/designModification.models';
import { ETransmittalStatus, ETransmittalType } from './transmittal.constants';
import { ITransmittalPurpose } from './transmittalPurpose/transmittalPurpose.models';

export interface ITransmittalItem {
  id: number;
  masterScheduleId: number;
  masterSchedule: IMasterScheduleOption;
  projectId: number;
  transmittalNo?: string;
  date: string;
  type: string;
  status?: ETransmittalStatus;
  sendFromProjectCompanyId: number;
  sendFromProjectCompany: IProjectCompanyBase;
  sendToProjectCompanyId: number;
  sendToProjectCompany: IProjectCompanyBase;
  sendDate: string;
  purposeId: number;
  purpose: ITransmittalPurpose;
  twcReleased?: boolean;
  rowVersion?: string;
  isArchived?: boolean;
}

export interface ITransmittalSearchFilter {
  trasnmittalNo?: StringFilter;
  t1RefNo?: StringFilter;
  transmittalType?: StringsFilter;
  designPackage?: StringFilter;
  date?: DateTimeFilter;
  status?: StringFilter;

  sendFrom?: OptionsFilter;
  sendTo?: OptionsFilter;

  nominatedDesigner?: OptionsFilter;
  nominatedChecker?: OptionsFilter;
  nominatedTWC?: OptionsFilter;

  releasedForContstruction?: BooleanFilter;
}

export interface ITransmittalTypeOption {
  code: ETransmittalType;
  name: string;
  formName: string;
}

export interface IUpsertProjectTransmittalRequest {
  projectId: number;
  masterScheduleId: number;
  remark?: string;
  type?: ETransmittalType;
  siteTransmittalId?: number;
}

export interface ICreateProjectTransmittalRequest extends IUpsertProjectTransmittalRequest {}
export interface IUpdateProjectTransmittalRequest extends IUpsertProjectTransmittalRequest, IUpdateRequestBase {
  purposeId: number;
  rseUserProfileId?: number;
  designModifications?: IUpsertProjectTransmittalDesignModification[];
}

export interface IDeleteProjectTransmittalRequest extends IApiBaseProps {
  id: number;
  masterScheduleId?: number;
}

export interface IProjectTransmittalBase {
  id: number;
  projectId: number;
  masterScheduleId: number;
  sendFromProjectCompanyId: number;
  sendToProjectCompanyId: number;
  sendDate: string;
  transmittalNo: string;
  purposeId: number;
  remark?: string;
  status?: ETransmittalStatus;
  approvedByProjectUserProfileId?: number;
  approvedDate?: string;
  type?: ETransmittalType;
  iceDesignCertRev?: boolean;
  siteTransmittalId?: number;
  rseUserProfileId?: number;
  iceUserProfileId?: number;
  iceApprovedDate?: string;
  twcReleased?: boolean;
  twcProjectUserProfileId?: number;
  twcReleasedDate?: string;
  rowVersion: string;
}

export interface IProjectTransmittal extends IProjectTransmittalBase {
  siteTransmittal?: IProjectTransmittalBase;
  purpose?: ITransmittalPurpose;
  inverseCheckerTransmittal?: IProjectTransmittalBase[];
  sendFromProjectCompany?: IProjectCompany;
  sendToProjectCompany?: IProjectCompany;
  approvedByProjectUserProfile?: IProjectUserProfile;
  rseUserProfile?: IUserProfile;
  iceUserProfile?: IUserProfile;
  twcProjectUserProfile?: IProjectUserProfile;
}
export interface IProjectTransmittalDetail extends IProjectTransmittal {
  siteTransmittal?: IProjectTransmittal;
  inverseCheckerTransmittal?: IProjectTransmittal[];
  purpose: ITransmittalPurpose;
  designModifications?: IProjectDesignModificationWithLink[];
  canRfc: boolean;
  project_LambethReviewerResponse?: IProjectLambethReviewerResponse[];
  isArchived?: boolean;
}

export interface IProjectLambethReviewerResponse {
  id: number;
  projectId: number;
  masterScheduleId: number;
  transmittalId: number;
  reviewNo: string;
  status: string;
  remark?: string;
  designerId: number;
  designerDate?: string;
  rowVersion?: string;
  createdDateTime: string;
}

export interface IProjectTransmittalDesignModificationBase {
  id: number;
  transmittalId: number;
  projectDesignModificationId: number;
  rowVersion?: string;
}
export interface IProjectTransmittalDesignModification extends IProjectTransmittalDesignModificationBase {}
export interface IUpsertProjectTransmittalDesignModification
  extends Omit<IProjectTransmittalDesignModificationBase, 'id' | 'projectDesignModificationId'> {
  id?: number;
  projectDesignModificationId?: number;
}

export interface IProjectTransmittalDistribution {
  distributionTeam?: IProjectTransmittalDistributionTeam[];
  distributionRole?: IProjectTransmittalDistributionRole[];
  distributionUser?: IProjectTransmittalDistributionUser[];
}
export interface IProjectTransmittalDistributionTeamBase {
  id: number;
  projectId: number;
  masterScheduleId: number;
  transmittalId: number;
  projectCompanyId: number;
  projectRoleId: number;
  rowVersion?: string;
}

export interface IProjectTransmittalDistributionRoleBase {
  id: number;
  projectId: number;
  masterScheduleId: number;
  transmittalId: number;
  projectCompanyId: number;
  projectTeamId: number;
  rowVersion?: string;
}

export interface IProjectTransmittalDistributionRole extends IProjectTransmittalDistributionRoleBase {
  projectCompany: IProjectCompanyBase;
  projectRole?: IProjectRole;
  projectTeam?: IProjectTeamBase;
  projectUserProfiles?: IProjectUserProfile[];
}

export interface IProjectTransmittalDistributionTeam extends IProjectTransmittalDistributionTeamBase {
  projectCompany: IProjectCompanyBase;
  projectRole?: IProjectRole;
  projectTeam?: IProjectTeamBase;
  projectUserProfiles?: IProjectUserProfile[];
}

export interface IProjectTransmittalDistributionUserBase {
  id: number;
  projectId: number;
  masterScheduleId: number;
  transmittalId: number;
  projectUserProfileId: number;
  rowVersion?: string;
}

export interface IProjectTransmittalDistributionUser extends IProjectTransmittalDistributionUserBase {
  projectUserProfile: IProjectUserProfile;
}

export interface ICreateProjectTransmittalDistributionTeamRequest {
  projectId: number;
  masterScheduleId: number;
  transmittalId: number;
  transmittalType?: string;
  projectCompanyId: number;
  projectTeamId: number;
}

export interface ICreateProjectTransmittalDistributionRoleRequest {
  projectId: number;
  masterScheduleId: number;
  transmittalId: number;
  transmittalType?: string;
  projectCompanyId: number;
  projectRoleId: number;
}

export interface ICreateProjectTransmittalDistributionUserRequest {
  projectId: number;
  masterScheduleId: number;
  transmittalId: number;
  projectUserProfileId: number;
}

export interface IDeleteProjectTransmittalDistributionRequest extends IApiBaseProps {
  transmittalId?: number;
  id: number;
}

export interface IProjectTransmittalOption extends IProjectTransmittalBase {
  masterSchedule: IProjectMasterScheduleBase;
}

export enum ETransmittalSearchKey {
  t1RefNo = 'MasterSchedule.RefNo',
  t1DesignPackage = 'MasterSchedule.DesignPackage',
  t1TwcId = 'MasterSchedule.Twc',
  t1Designers = 'MasterSchedule.Designers',
  t1Checkers = 'MasterSchedule.Checkers',
  transmittalNo = 'TransmittalNo',
  type = 'type',

  sendDate = 'SendDate',
  status = 'Status',
  sendFromProjectCompanyId = 'SendFromProjectCompany',
  sendToProjectCompanyId = 'SendToProjectCompany',
  twcReleased = 'TwcReleased',
}
